import _ from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';

import { useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { type ITimeMachine, createTimeMachine } from '@treams/treams-ui';

import CycleMomentsPreviewView from './CycleMomentsPreviewView';

const transformToMomentRepresentable = (moments: graphql.MomentInstance[]) => {
	return moments.map((momentInstance) => ({
		startDate: moment(momentInstance.startDate).toDate(),
		endDate: moment(momentInstance.endDate).toDate(),
		title: momentInstance.momentTemplate.type,
		cycle: momentInstance.cycleTemplate,
		cycleInstanceID: momentInstance.cycleInstance.id,
		template: momentInstance.momentTemplate,
		type: momentInstance.momentTemplate.type,
		instance: momentInstance as graphql.MomentInstance | undefined,
		status: momentInstance.status,
	})) as IMomentRepresentable[];
};

interface IMomentRepresentable {
	startDate: Date;
	endDate: Date;
	title: string;
	cycle: graphql.CycleTemplate;
	cycleInstanceID: string;
	template: graphql.MomentTemplate;
	type: graphql.MomentType;
	instance?: graphql.MomentInstance;
	status: graphql.MomentStatus;
}

const projectUpcomingMoments = (
	cycles: graphql.CycleInstance[],
	[start, end]: [Date, Date],
	moments: graphql.MomentInstance[],
	showMessageMoments: boolean,
) => {
	const upcomingMoments: IMomentRepresentable[] = [];

	for (const cycle of cycles) {
		// If a cycle activates after the end date of the bounds, we don't
		// render any upcoming moments for this cycle
		if ((cycle.activatesAt as Date) > end) {
			continue;
		}

		const startDate = moment.utc(cycle.startDate);
		let activatesAt = moment.utc(cycle.activatesAt);
		const deactivatesAt = cycle.deactivatesAt ? moment.utc(cycle.deactivatesAt) : undefined;
		let isRepeated = false;
		const durationInDays = Math.round((365 / 12) * cycle.cycleTemplate.duration);

		// If the cycle already has the repeated cycle scheduled
		// don't show the moments
		if (
			cycle.succeededBy ||
			moment(startDate)
				.add(durationInDays - 1, 'days')
				.isBefore(moment(), 'day')
		) {
			continue;
		}

		while (activatesAt.isBefore(end)) {
			// eslint-disable-next-line no-restricted-syntax
			for (const momentTemplate of cycle.cycleTemplate.moments) {
				// If it's a message moment and it shouldn't be shown to the user,
				// only show if it's a superuser
				if (
					momentTemplate.type === graphql.MomentType.Message &&
					!momentTemplate.showToUser &&
					!showMessageMoments
				) {
					continue;
				}

				// Don't show moment if it is skipped beforehand
				if (
					cycle.skippedFutureMoments?.some(
						(skipped) => skipped.momentTemplateID === momentTemplate.id,
					)
				) {
					continue;
				}

				// If this moment template already has a matching instance, don't return it either
				if (
					cycle.precededBy &&
					!isRepeated &&
					moments.some(
						(momentInstance) => momentInstance.momentTemplate.original?.id === momentTemplate.id,
					)
				) {
					continue;
				}

				// Check if the date of this moment fits within the bounds, and is after the activatesAt and before
				// the deactivatesAt date
				const momentStartDate = moment(startDate).add(momentTemplate.startOffset, 'days');

				if (
					momentStartDate.isBefore(activatesAt) ||
					(deactivatesAt && momentStartDate.isSameOrAfter(deactivatesAt)) ||
					momentStartDate.isBefore(start) ||
					momentStartDate.isAfter(end)
				) {
					continue;
				}

				// Push the current moment template to the array
				upcomingMoments.push({
					startDate: momentStartDate.toDate(),
					endDate: moment(momentStartDate).add(momentTemplate.duration, 'days').toDate(),
					title: momentTemplate.type,
					cycle: cycle.cycleTemplate,
					cycleInstanceID: cycle.id,
					template: momentTemplate,
					type: momentTemplate.type,
					status: graphql.MomentStatus.Open,
				});
			}

			// If the cycle doesn't repeat, we can break here
			if (!cycle.cycleTemplate.repeats) {
				break;
			}

			// Only repeat the projection when the template repeats with itself.
			// Cross-template succession is handled by a separate cycle instance,
			// so we must not re-project this template's moments for future periods.
			const successorID = cycle.cycleTemplate.successor?.id;
			const repeatsWithSelf = !!successorID && successorID === cycle.cycleTemplate.id;

			if (!repeatsWithSelf) break;

			// If the cycle does repeat, take the start date and add the duration
			// and keep doing so until we've reached the end bound
			isRepeated = true;
			startDate.add(durationInDays, 'days');
			if (activatesAt.isBefore(startDate)) {
				activatesAt = moment(startDate);
			}
			deactivatesAt?.add(durationInDays, 'days');
		}
	}

	return upcomingMoments;
};

export type MomentPerspective = 'viewer' | 'employee';

// Accounts for the instance state to determine moment status.
// The backend cycle only takes the first step (creating an instance) for status.
function momentStatusFromInstance(
	momentInstance: IMomentRepresentable,
	perspective: MomentPerspective,
) {
	if (momentInstance.instance?.evaluation) {
		const { evaluation } = momentInstance.instance;

		const party = perspective === 'employee' ? evaluation.employee : evaluation.viewer;

		const { status, requiresSignature } = party;

		if (!requiresSignature) {
			return status === graphql.EvaluationSubmissionStatus.Draft
				? graphql.MomentStatus.Open
				: graphql.MomentStatus.Completed;
		}

		if (status !== graphql.EvaluationSubmissionStatus.Signed) {
			return graphql.MomentStatus.Open;
		}
	}

	return momentInstance.status;
}

export function isCycleDue(
	user: graphql.User,
	includeMessageMoments: boolean,
	bounds: [Date, Date],
) {
	const now = moment.utc().startOf('day');

	const upcomingMoments = projectUpcomingMoments(
		user.cycles.edges.map(({ node }) => node) as unknown as graphql.CycleInstance[],
		bounds,
		user.moments as unknown as graphql.MomentInstance[],
		includeMessageMoments,
	);

	const moments = transformToMomentRepresentable(
		user.moments as unknown as graphql.MomentInstance[],
	)
		.concat(upcomingMoments)
		.map((momentInstance) => ({
			...momentInstance,
			status: momentStatusFromInstance(momentInstance, 'viewer'),
		}))
		.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
		.filter(
			(momentInstance) =>
				(momentInstance.template.type !== graphql.MomentType.Message ||
					momentInstance.template.showToUser ||
					includeMessageMoments) &&
				// End date is after the lower bound
				moment(momentInstance.endDate).isAfter(bounds[0]) &&
				// Start date is before the upper bound
				moment(bounds[1]).isAfter(moment(momentInstance.startDate)),
		);

	const momentsWithDue = moments.map((momentInstance) => ({
		...momentInstance,
		isDue:
			momentInstance.status === graphql.MomentStatus.Open &&
			now.clone().add(1, 'days').startOf('day').isAfter(momentInstance.endDate),
	}));

	const momentsDue = momentsWithDue.filter((momentInstance) => momentInstance.isDue);

	return !!momentsDue.length;
}

export function CycleMomentsPreview({
	dateRange,
	user,
	cycles,
	cyclesLoading,
	moments,
	momentsLoading,
	canSendReminder = false,
	mini = false,
	embedded = false,
	allowPagination,
	perspective = 'viewer',
	timeMachineValue,
	onChangeTimeMachine,
}: {
	dateRange?: [Date, Date];
	user?: graphql.User;
	canSendReminder?: boolean;
	mini?: boolean;
	embedded?: boolean;
	allowPagination?: boolean;
	cycles?: graphql.CycleInstance[];
	cyclesLoading?: boolean;
	moments?: graphql.MomentInstance[];
	momentsLoading?: boolean;
	perspective?: MomentPerspective;
	timeMachineValue?: ITimeMachine;
	onChangeTimeMachine?: (value: ITimeMachine) => void;
}) {
	const { t } = useTranslation();
	const roles = useViewerRoles();

	const showMessageMoments = roles.isWorkspaceSuperuser;

	const data = {
		cycles: cycles ?? user?.cycles?.edges.map(({ node }) => node) ?? [],
		moments: moments ?? user?.moments ?? [],
	};

	const now = moment.utc().startOf('day');

	const currentDate = now.clone().startOf('day').toDate();
	const bounds = dateRange ?? [now.clone().toDate(), now.clone().toDate()];

	const upcomingMoments = projectUpcomingMoments(
		data.cycles,
		bounds,
		data.moments,
		showMessageMoments,
	);

	const representableMoments = transformToMomentRepresentable(
		data.moments.filter((momentInstance) => momentInstance.status !== graphql.MomentStatus.Skipped),
	)
		.concat(upcomingMoments)
		.map((momentInstance) => ({
			...momentInstance,
			status: momentStatusFromInstance(momentInstance, perspective),
			title: t(`common.module.${t(_.upperFirst(momentInstance.title.toLowerCase()))}`),
		}))
		.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
		.filter(
			(momentInstance) =>
				(momentInstance.template.type !== graphql.MomentType.Message ||
					momentInstance.template.showToUser ||
					showMessageMoments) &&
				// End date is after the lower bound
				moment(momentInstance.endDate).isAfter(bounds[0]) &&
				// Start date is before the upper bound
				moment(bounds[1]).isAfter(moment(momentInstance.startDate)),
		);

	const momentsWithDue = representableMoments.map((momentInstance) => ({
		...momentInstance,
		isDue:
			momentInstance.status === graphql.MomentStatus.Open &&
			now.clone().add(1, 'days').startOf('day').isAfter(momentInstance.endDate),
	}));

	const momentsDue = momentsWithDue.filter((momentInstance) => momentInstance.isDue);
	const firstMomentDue = momentsDue.length > 0 ? momentsDue[0].startDate : undefined;

	const loading =
		(typeof cyclesLoading !== 'boolean' && typeof momentsLoading !== 'boolean' && !user) ||
		cyclesLoading ||
		(momentsLoading && bounds[0] < new Date());

	return (
		<CycleMomentsPreviewView
			bounds={bounds}
			firstMomentDue={firstMomentDue}
			currentDate={currentDate}
			momentsWithDue={momentsWithDue}
			canSendReminder={canSendReminder}
			mini={mini}
			embedded={embedded}
			loading={loading}
			allowPagination={allowPagination}
			timeMachineValue={timeMachineValue}
			onChangeTimeMachine={onChangeTimeMachine}
		/>
	);
}

type CycleMomentsPreviewIDProps = {
	monthsRange?: [number, number];
	userID: string;
};

export function useLocalStorageTimeMachine(): {
	timeMachine: ITimeMachine;
	setTimeMachine: (value: ITimeMachine) => void;
	bounds: [Date, Date];
} {
	const initial = createTimeMachine('month', new Date(), undefined, true);

	const [timeMachine, setTimeMachine] = useLocalStorage('timeMachine', initial);

	useEffect(() => {
		// Check if the current date is within the timeframe of the timeMachine, if not, reset the timeMachine
		// to it's initial value.
		if (!moment().isBetween(timeMachine.current[0], timeMachine.current[1], 'day', '()')) {
			// If a custom interval in the past or future was selected, reset to the initial value.
			if (timeMachine.interval === 'custom') {
				setTimeMachine(initial);
				return;
			}
			// If a month, week or quarter interval was selected, reset to the current timeframe
			// using that interval.
			setTimeMachine(createTimeMachine(timeMachine.interval, new Date(), undefined, true));
		}
	}, []);

	const bounds = timeMachine.current;

	return { timeMachine, setTimeMachine, bounds };
}

export default function CycleMomentsPreviewID({ userID }: CycleMomentsPreviewIDProps) {
	const { timeMachine, setTimeMachine, bounds } = useLocalStorageTimeMachine();

	const { data: cyclesData, loading: cyclesLoading } = graphql.useUserWithCyclesQuery({
		variables: {
			id: userID,
			status: [
				graphql.CycleStatus.Pending,
				graphql.CycleStatus.Active,
				graphql.CycleStatus.Completed,
			],
		},
	});

	const { data: momentsData, loading: momentsLoading } = graphql.useUserWithMomentsQuery({
		variables: { id: userID, after: bounds[0], before: bounds[1] },
	});

	return (
		<CycleMomentsPreview
			moments={momentsData?.user?.moments as graphql.MomentInstance[]}
			momentsLoading={momentsLoading}
			cycles={cyclesData?.user?.cycles?.edges.map(({ node }) => node) as graphql.CycleInstance[]}
			cyclesLoading={cyclesLoading}
			dateRange={bounds}
			allowPagination
			timeMachineValue={timeMachine}
			onChangeTimeMachine={(value) => setTimeMachine(value)}
		/>
	);
}
